import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import API from '../api';
import { getSocket } from '../socket';
import { useAuth } from './AuthContext';
import { playSound } from '../utils/soundAlerts';

// ── 사내 대화방 전역 상태 ──────────────────────────────────────────────────
// 메인 창(사이드바) 전체를 감싸서, 어디서든 대화방 목록/안읽음 배지/알림 미리보기 팝업/채팅창
// 상태를 쓸 수 있게 한다. 채팅 화면은 좌측 대화상대 트리 + 우측 대화창이 하나로 합쳐진 단일 창
// (components/ChatMainWindow.js)이며, 사내 메신저 버튼을 누르면 열리고 닫힌다. 어떤 대화방을
// 보고 있는지(activeConversationId)도 여기서 관리해서, 사이드바/미리보기 팝업 어디서 클릭해도
// 같은 창에 그 대화가 표시된다.
//
// 알림은 두 단계로 동작한다: ① 새 메시지가 오면 버튼 위에 보낸사람(부서 직급 이름)과 내용을
// 5초간 살짝 보여주는 미리보기 팝업(flashPreview) — 넘겨보고 지나갈 수 있는 가벼운 알림.
// ② 그와 별개로 버튼의 "사내 메신저" 글자 옆 숫자 배지(unreadTotal)는 실제로 그 대화를 읽기
// 전까지 계속 남아있음 — 이것이 "확인 전까지 유지되는 알림" 요구사항을 satisfies한다.
const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [flashPreview, setFlashPreview] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(() => new Set());
  const [mainWindowOpen, setMainWindowOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [memberMap, setMemberMap] = useState({});
  const flashTimerRef = useRef(null);
  // 소켓 이벤트 핸들러(onNewMessage 등)는 마운트 시 한 번만 등록되므로, 그 안에서 "지금 이 대화방을
  // 보고 있는 중인가"를 최신 값으로 판단하려면 state를 직접 클로저로 참조하면 안 되고(등록 당시
  // 값에 고정됨) ref로 항상 최신값을 읽어야 한다.
  const mainWindowOpenRef = useRef(mainWindowOpen);
  const activeConversationIdRef = useRef(activeConversationId);
  useEffect(() => { mainWindowOpenRef.current = mainWindowOpen; }, [mainWindowOpen]);
  useEffect(() => { activeConversationIdRef.current = activeConversationId; }, [activeConversationId]);

  const refreshConversations = useCallback(() => {
    if (!user) return;
    API.get('/messages/conversations').then(r => setConversations(r.data || [])).catch(() => {});
  }, [user]);

  useEffect(() => { refreshConversations(); }, [refreshConversations]);

  // 미리보기 팝업에 "부서 직급 이름"을 보여주기 위한 회원정보(1회 조회)
  useEffect(() => {
    if (!user) return;
    API.get('/messages/members').then(r => {
      const map = {};
      (r.data || []).forEach(m => { map[m.username] = m; });
      setMemberMap(map);
    }).catch(() => {});
  }, [user]);

  const labelFor = useCallback((username, fallbackName) => {
    const info = memberMap[username];
    if (!info) return fallbackName || username;
    const nameLine = [info.position, info.name].filter(Boolean).join(' ') || fallbackName || username;
    return info.department ? `${info.department} ${nameLine}` : nameLine;
  }, [memberMap]);

  const showFlash = useCallback((preview) => {
    setFlashPreview(preview);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashPreview(null), 5000);
  }, []);

  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  useEffect(() => {
    if (!user) return;
    const s = getSocket();
    if (!s) return;

    const onNewMessage = (msg) => {
      // 지금 이 대화방을 실제로 펼쳐서 보고 있는 중이면(채팅창 열림 + 그 대화방이 활성 상태)
      // 안읽음 숫자를 올리지도, 미리보기 팝업을 띄우지도 않는다 — 어차피 바로 화면에 보이고
      // ConversationPane이 즉시 읽음처리를 하기 때문. 이 값이 빠져있던 것이 "대화창에서는
      // 읽음으로 나오는데 런처 버튼의 숫자 배지는 그대로 남아있던" 버그의 원인이었다.
      const alreadyViewing = mainWindowOpenRef.current && activeConversationIdRef.current === msg.conversation_id;

      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === msg.conversation_id);
        if (idx === -1) { refreshConversations(); return prev; }
        const updated = { ...prev[idx], lastMessage: msg };
        if (msg.sender_username !== user.username && !alreadyViewing) {
          updated.unreadCount = (prev[idx].unreadCount || 0) + 1;
        }
        const next = prev.slice();
        next.splice(idx, 1);
        next.unshift(updated);
        return next;
      });

      if (msg.sender_username === user.username) return; // 내가 보낸 메시지는 알림 필요 없음
      if (!alreadyViewing) {
        playSound('message');
        showFlash({
          conversationId: msg.conversation_id,
          sender: labelFor(msg.sender_username, msg.sender_name),
          preview: msg.type === 'text' ? msg.content
            : msg.type === 'image' ? '📷 사진을 보냈습니다'
            : `📎 ${msg.file_name || '파일'}을 보냈습니다`,
        });
      }
    };

    // 메시지가 삭제되면(=상대가 아직 안읽었을 때만 가능) 안읽음 숫자도 그만큼 줄어야 한다.
    // 직접 -1 계산하는 대신 서버에 안읽음 개수를 다시 물어봐서(refreshConversations) 정확하게
    // 맞춘다 — 서버의 GET /conversations가 "삭제되지 않고 남아있는 메시지 중 아직 안읽은 것"을
    // COUNT(*)로 다시 세어주므로, 클라이언트에서 직접 빼는 것보다 항상 실제 값과 일치한다.
    const onMessageDeleted = () => refreshConversations();

    const onConversationNew = () => refreshConversations();
    const onConversationUpdated = () => refreshConversations();
    const onConversationRenamed = ({ id, name }) => {
      setConversations(prev => prev.map(c => c.id === id ? { ...c, name } : c));
    };
    const onPresence = ({ username, online }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        if (online) next.add(username); else next.delete(username);
        return next;
      });
    };
    // 접속하는 순간 서버가 "지금 온라인인 사람 전체 목록"을 스냅샷으로 한 번 보내준다 — 나보다
    // 먼저 접속해있던 사람들의 온라인 상태를 놓치지 않기 위함(브라우저를 나눠 여러 창을 띄웠을 때
    // 한쪽에서는 온라인 표시가 되고 다른 쪽에서는 안 되던 문제의 원인).
    const onPresenceSnapshot = (usernames) => setOnlineUsers(new Set(usernames || []));

    s.on('message:new', onNewMessage);
    s.on('message:deleted', onMessageDeleted);
    s.on('conversation:new', onConversationNew);
    s.on('conversation:updated', onConversationUpdated);
    s.on('conversation:renamed', onConversationRenamed);
    s.on('presence:update', onPresence);
    s.on('presence:snapshot', onPresenceSnapshot);
    return () => {
      s.off('message:new', onNewMessage);
      s.off('message:deleted', onMessageDeleted);
      s.off('conversation:new', onConversationNew);
      s.off('conversation:updated', onConversationUpdated);
      s.off('conversation:renamed', onConversationRenamed);
      s.off('presence:update', onPresence);
      s.off('presence:snapshot', onPresenceSnapshot);
    };
  }, [user, refreshConversations, labelFor, showFlash]);

  // 채팅창을 열고 특정 대화방을 활성화한다(사이드바/미리보기 팝업/대화상대초대 등 어디서 호출해도 동일).
  // 대화방을 여는 순간 그 대화방은 곧바로 읽음 처리되므로(ConversationPane이 즉시
  // /read를 호출), 안읽음 배지 숫자도 여기서 바로 0으로 비워준다.
  // 대화방 안읽음 배지를 0으로 비운다. openConversationWindow(대화상대 클릭으로 새로 여는 경우)뿐
  // 아니라, 이미 activeConversationId로 지정돼 있던 대화방을 다시 열 때(런처 버튼으로 창을 다시
  // 펼치는 경우 등 openConversationWindow를 거치지 않는 경로)에도 실제로 메시지를 읽어서 서버에
  // /read가 호출되는 시점마다 함께 불러줘야 한다 — 그렇지 않으면 서버의 읽음 상태는 갱신됐는데
  // 런처 버튼의 숫자 배지만 그대로 남아있는 버그가 생긴다(ConversationPane의 markRead에서 호출됨).
  const markConversationRead = useCallback((conversationId) => {
    setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, unreadCount: 0 } : c));
  }, []);

  const openConversationWindow = useCallback((conversationId) => {
    setActiveConversationId(conversationId);
    setMainWindowOpen(true);
    setFlashPreview(prev => (prev && prev.conversationId === conversationId ? null : prev));
    markConversationRead(conversationId);
  }, [markConversationRead]);

  const closeMainWindow = useCallback(() => setMainWindowOpen(false), []);
  const toggleMainWindow = useCallback(() => setMainWindowOpen(o => !o), []);

  const openDirectChat = useCallback(async (peerUsername) => {
    const r = await API.post('/messages/conversations', { type: 'direct', peerUsername });
    openConversationWindow(r.data.id);
    refreshConversations();
  }, [openConversationWindow, refreshConversations]);

  const createGroupChat = useCallback(async (name, memberUsernames) => {
    const r = await API.post('/messages/conversations', { type: 'group', name, memberUsernames });
    openConversationWindow(r.data.id);
    refreshConversations();
  }, [openConversationWindow, refreshConversations]);

  const renameGroup = useCallback(async (conversationId, name) => {
    await API.put(`/messages/conversations/${conversationId}`, { name });
    setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, name } : c));
  }, []);

  const inviteToConversation = useCallback(async (conversationId, usernames) => {
    await API.post(`/messages/conversations/${conversationId}/invite`, { usernames });
    refreshConversations();
  }, [refreshConversations]);

  const dismissFlash = useCallback(() => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setFlashPreview(null);
  }, []);

  // ⚠️ setState의 updater 함수 안에서 또 다른 setState(openConversationWindow는 내부적으로
  // setActiveConversationId/setMainWindowOpen/setConversations를 호출함)를 연쇄로 호출하면
  // React가 그 갱신들을 안정적으로 반영하지 못할 수 있다(렌더링 중 부수효과 호출 금지 규칙 위반).
  // 이게 바로 "알림 팝업을 클릭하면 창은 열리는데 숫자 배지는 안 사라지던" 버그의 원인이었다 —
  // openConversationWindow가 setFlashPreview의 updater 함수 안에서 호출되면서 그 안의
  // setConversations(안읽음 0으로 비우기) 호출이 씹히는 경우가 있었다. state를 직접 읽어서
  // 순서대로 호출하는 평범한 이벤트 핸들러 형태로 바꾼다.
  const openFromFlash = useCallback(() => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    if (flashPreview) openConversationWindow(flashPreview.conversationId);
    setFlashPreview(null);
  }, [flashPreview, openConversationWindow]);

  const unreadTotal = conversations.reduce((a, c) => a + (c.unreadCount || 0), 0);

  return (
    <ChatContext.Provider value={{
      conversations, unreadTotal, flashPreview, onlineUsers,
      mainWindowOpen, activeConversationId,
      refreshConversations, openDirectChat, createGroupChat, renameGroup, inviteToConversation,
      openConversationWindow, markConversationRead, closeMainWindow, toggleMainWindow,
      dismissFlash, openFromFlash,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  return useContext(ChatContext);
}